import classNames from 'classnames'
import { groupBy } from 'lodash'
import { type ReactNode, useEffect, useState } from 'react'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'

/**
 * Async picker for owner/contact selection, extracted from TasksInbox for reuse
 * (task detail pane, Action Center focus card). Fetches a small page via
 * GraphQL on open and re-fetches as the user types in the search box. Server
 * filters by ES query; we set shouldFilter=false on Command so it doesn't
 * double-filter client-side.
 */
/** A row in the list. `subtitle` is the second line: whatever tells two same-named records apart.
 *  Without one, an email is used when there is also a name. */
export type PickerOption = {
  id: string
  name?: string
  email?: string
  subtitle?: string
  /** Rows sharing a group render under that heading, in first-seen order. */
  group?: string
  disabled?: boolean
  icon?: ReactNode
}

export function InlineAsyncPicker({
  entity,
  baseSearch,
  fetcher,
  display,
  onSave,
  searchPlaceholder = `Search…`,
  emptyLabel = `No results`,
  popoverClassName,
  triggerClassName,
}: {
  entity: 'users' | 'contacts'
  baseSearch?: string
  /**
   * Custom loader replacing the default entity fetch — e.g. `usersFetcher`
   * scoped to a workspace so only valid assignees are offered. Callers must
   * memoize it (it's an effect dependency).
   */
  fetcher?: (params: { query: string }) => Promise<{ hits: PickerOption[] }>
  display: React.ReactNode
  onSave: (id: string | null, name?: string) => void | Promise<unknown>
  searchPlaceholder?: string
  emptyLabel?: string
  popoverClassName?: string
  /**
   * Override the default trigger styling. Pass an empty string or override
   * classes to disable the built-in hover background when the caller wants
   * to render their own chip / chrome inside `display`.
   */
  triggerClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState(``)
  const [options, setOptions] = useState<PickerOption[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    const load = () => {
      if (fetcher) return fetcher({ query: searchValue })
      const parts = [baseSearch, searchValue ? `name:*${searchValue}*` : ``].filter(Boolean) as string[]
      const search = parts.length ? parts.join(` AND `) : undefined
      const fields = entity === `users` ? `id name email` : `id name`
      // Use explicit `fields` override to bypass MODEL_FIELDS[entity] — for `users`
      // the default includes the broken `role` resolver that throws and nullifies every hit.
      return gqlFetchList<{ id: string; name?: string; email?: string }>(
        entity,
        {
          search,
          pagination: { page: 0 },
        },
        fields,
      )
    }
    bg(
      load().then((result) => {
        if (cancelled) return
        const hits = result?.hits ?? []
        setOptions(hits)
        setLoading(false)
      }),
    )
    return () => {
      cancelled = true
    }
  }, [open, searchValue, baseSearch, entity, fetcher])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={
            triggerClassName ??
            `-mx-2 -my-1 flex w-full items-center rounded px-2 py-1 text-left transition-colors hover:bg-surface-muted/60`
          }
        >
          {display}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className={classNames(`w-[280px] p-0`, popoverClassName)}>
        <Command shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} value={searchValue} onValueChange={setSearchValue} />
          <CommandList>
            {loading ? (
              <div className="p-3 text-center text-body-sm text-subtle">Loading…</div>
            ) : options.length === 0 ? (
              <CommandEmpty>{emptyLabel}</CommandEmpty>
            ) : (
              Object.entries(groupBy(options, (opt) => opt.group ?? ``)).map(([heading, group]) => (
                <CommandGroup key={heading} heading={heading || undefined}>
                  {group.map((opt) => (
                    <CommandItem
                      key={opt.id}
                      value={opt.id}
                      disabled={opt.disabled}
                      onSelect={() => {
                        // Close first: if onSave kicks off parent re-renders, the
                        // close must not depend on them settling.
                        setOpen(false)
                        onSave(opt.id, opt.name)
                      }}
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        {opt.icon}
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate">{opt.name || opt.email || opt.id}</span>
                          {(opt.subtitle || (opt.name && opt.email)) && (
                            <span className="truncate text-body-xs text-subtle">{opt.subtitle || opt.email}</span>
                          )}
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
